import TR from 'utils/TR';
import T from 'utils/T';
import ex from 'utils/ex';
import React from 'react';
import useRect from 'hooks/common/useRect';
import { Chart } from 'chart.js';
import * as Luxon from 'luxon'

import useReportMealDailyGraph from 'hooks/communication/useReportMealDailyGraph';
import Recoil from 'recoil';
import * as AppStore from 'stores/AppStore';
import useSetChartData from 'hooks/app/useSetChartData';

const config = (hash, onComplete, duration) => ({
  type: 'bar',
  data: {
    labels: hash.labels,
    datasets: [
      {
        // barThickness: 20,
        ...(hash.borderRadius_carbo ? { borderRadius: 10 } : {}),
        label: T('탄수화물'),
        data: hash.carbo_intaken_cals,
        backgroundColor: '#5F9FFF',
      }, {
        // barThickness: 20,
        ...(hash.borderRadius_protein ? { borderRadius: 10 } : {}),
        label: T('단백질'),
        data: hash.protein_intaken_cals,
        backgroundColor: '#FF8B70',
      }, {
        borderWidth: 0,
        borderRadius: 10,
        // barThickness: 20,
        label: T('지방'),
        data: hash.fat_intaken_cals,
        backgroundColor: '#FFCA7E',
      },
    ]
  },
  options: {
    devicePixelRatio: 4,
    animation: {
      duration,
      onComplete,
    },
    layout: {
      padding: {
        top: 30,
        left: 30,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        border: {
          dash: [4, 4],
        },
        stacked: true,
        grid: {
          display: true,
          drawTicks: false,
          drawOnChartArea: true,
        },
        ticks: {
          display: true,
          font: {
            weight: 400,
            size: 11,
            color: '#000000',
          },
          color: '#000000',
        },
      },
      y: {
        border: { display: false },
        stacked: true,
        grid: {
          display: true,
          drawTicks: false,
          drawOnChartArea: true,
        },
        min: 0,
        max: hash.max,
        ticks: {
          display: true,
          stepSize: 500,
          color: '#aaaaaa',
          font: {
            weight: 400,
            size: 11,
            color: '#aaaaaa',
          },
        },
      }
    }
  },
  plugins: [{
    afterDatasetDraw: (chart, args, options) => {
      const ctx = chart.ctx;
      const yAxis = chart.scales.y;
      const xAxis = chart.scales.x;

      const meta = args.meta;
      const dataset = meta.controller.getDataset();
      if (!dataset) return;

      if (!yAxis) return;
      if (!xAxis) return;

      const left = xAxis.left;
      const right = xAxis.right;

      ctx.save();

      ctx.font = '12px Pretendard';
      ctx.fillStyle = '#bbbbbb';
      const y = yAxis.getPixelForValue(hash.max);

      ctx.textAlign = 'right';
      ctx.textBaseline = 'bottom';
      ctx.fillText(T('칼로리(kcal)'), left - 3, y - 15);


      const y2000 = yAxis.getPixelForValue(2000);
      const text = T('2000kcal');
      const metrics = ctx.measureText(text)
      const { width } = metrics;
      const fontHeight = metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent;
      const height = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;

      ctx.strokeStyle = '#16B464';
      ctx.beginPath()
      ctx.moveTo(left - 5, y2000);
      ctx.lineTo(right, y2000);
      ctx.stroke();

      ctx.fillStyle = '#16B464';
      ctx.fillRect(left - 10 - width + 1, y2000 - height * 0.5 - 2, width + 1, height + 3);
      ctx.beginPath()
      ctx.moveTo(left - 4, y2000);
      ctx.lineTo(left - 8, y2000 - height * 0.5 - 2);
      ctx.lineTo(left - 8, y2000 + height * 0.5 + 1);
      ctx.lineTo(left - 4, y2000);
      ctx.fill();


      ctx.fillStyle = '#ffffff';
      ctx.font = '10px Pretendard';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, left - 10, y2000);

      ctx.restore();
    }
  }]
});

export default function DietStatisticsChart(props) {
  const {
    duration,
    ...rest
  } = props;

  const {
    requestReportMealDailyGraph,
  } = useReportMealDailyGraph();

  const [data, setData] = React.useState({ uninitialized: true });

  const setChartData = useSetChartData('DietStatisticsChart');
  const chartDataTimeoutRef = React.useRef();
  const onComplete = React.useCallback((animation) => {
    if (data.uninitialized) return;
    if (duration) return;
    if (!animation.chart.canvas.width) return;
    if (!animation.chart.canvas.height) return;
    clearTimeout(chartDataTimeoutRef.current);
    const url = animation.chart.canvas.toDataURL('image/png');
    const width = animation.chart.canvas.width;
    const height = animation.chart.canvas.height;
    chartDataTimeoutRef.current = setTimeout(() => {
      setChartData && setChartData(() => ({ url, width, height }));
      URL.revokeObjectURL(url);
    }, 6000);
  }, [data.uninitialized, duration, setChartData]);

  // const [daily_meal_stat_graph, setList] = React.useState([]);
  const period_start = Recoil.useRecoilValue(AppStore.assets.period_start);
  const period_end = Recoil.useRecoilValue(AppStore.assets.period_end);
  const patient_no = Recoil.useRecoilValue(AppStore.assets.patient_no);
  React.useEffect(() => {
    requestReportMealDailyGraph({
      patient_no,
      search_start_time: period_start,
      search_end_time: period_end,
    }, (success, data) => {
      if (success) {
        setData(() => data || {});
      }
    });
  }, [patient_no, period_end, period_start, requestReportMealDailyGraph]);

  const hash = React.useMemo(() => {
    const {
      tot_carbo_intaken_cal,
      tot_protein_intaken_cal,
      tot_fat_intaken_cal,
      daily_meal_stat_graph = [],
    } = data || {};
    const d = {
      labels: [T('전체')],
      carbo_intaken_cals: [tot_carbo_intaken_cal],
      protein_intaken_cals: [tot_protein_intaken_cal],
      fat_intaken_cals: [tot_fat_intaken_cal],
    };
    let max = tot_carbo_intaken_cal + tot_protein_intaken_cal + tot_fat_intaken_cal;
    daily_meal_stat_graph.forEach(item => {
      const {
        date,
        carbo_intaken_cal,
        protein_intaken_cal,
        fat_intaken_cal,
      } = item;
      const sum = carbo_intaken_cal + protein_intaken_cal + fat_intaken_cal;
      if (max < sum) max = sum;

      const label = Luxon.DateTime.fromFormat(date, 'MMdd').toFormat("M/d");
      d.labels.push(label);
      d.carbo_intaken_cals.push(carbo_intaken_cal);
      d.protein_intaken_cals.push(protein_intaken_cal);
      d.fat_intaken_cals.push(fat_intaken_cal);
      if (fat_intaken_cal === 0 && protein_intaken_cal === 0) {
        d.borderRadius_carbo = true;
      } else if (fat_intaken_cal === 0 && protein_intaken_cal !== 0) {
        d.borderRadius_protein = true;
      }
    });
    d.max = Math.max(2000, Math.ceil(max / 1000) * 1000);

    return d;
  }, [data]);

  const ref = React.useRef();
  const rect = useRect(ref);
  const canvasRef = React.useRef();
  const chartRef = React.useRef();
  React.useEffect(() => {
    const chart = new Chart(canvasRef.current, config(hash, onComplete, duration));
    chartRef.current = chart;
    return () => {
      chart.destroy();
      delete chart.config;
    };
  }, [duration, hash, onComplete]);

  React.useLayoutEffect(() => {
    if (!chartRef.current) return;
    if (rect.uninitialized) return;
    // console.log('rect', rect);
    const frameId = requestAnimationFrame(() => {
      chartRef.current.update();
    });
    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [rect]);
  return (
    <div
      {...ex(['Chart', 'DietStatisticsChart'], props)}
      {...{ ref }}
    >
      <canvas
        {...{
          ref: canvasRef,
          width: rect.width,
          height: rect.height,
        }}
      />
    </div>
  );
}
